import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Wallet,
  Calendar,
  Loader2,
  Receipt,
  TrendingUp,
  Eye,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = "/api";

/**
 * READ-ONLY tutor salary page.
 *
 * Tutors see only THEIR OWN records (backend enforces this via auth — the
 * frontend never sends a teacher_id; the backend derives it from the JWT).
 *
 * All amounts displayed in PHP (₱). Tutors cannot edit anything.
 */
export default function MySalary() {
  const { getAuthHeader } = useAuth();

  // Default to current calendar month
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString().slice(0, 10);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    .toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(monthStart);
  const [endDate, setEndDate] = useState(monthEnd);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandHistory, setExpandHistory] = useState(false);
  const [allTimeData, setAllTimeData] = useState(null);
  const [allTimeLoading, setAllTimeLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tutor/my-salary`, {
        params: { start_date: startDate, end_date: endDate },
        headers: getAuthHeader(),
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load salary");
    } finally {
      setLoading(false);
    }
  };

  const loadAllTime = async () => {
    if (allTimeData) return; // already loaded
    setAllTimeLoading(true);
    try {
      const r = await axios.get(`${API}/tutor/my-salary`, {
        headers: getAuthHeader(),
      });
      setAllTimeData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally {
      setAllTimeLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quick-pick presets
  const setPreset = (preset) => {
    const t = new Date();
    if (preset === "thisMonth") {
      setStartDate(new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10));
      setEndDate(new Date(t.getFullYear(), t.getMonth() + 1, 0).toISOString().slice(0, 10));
    } else if (preset === "lastMonth") {
      setStartDate(new Date(t.getFullYear(), t.getMonth() - 1, 1).toISOString().slice(0, 10));
      setEndDate(new Date(t.getFullYear(), t.getMonth(), 0).toISOString().slice(0, 10));
    } else if (preset === "first15") {
      setStartDate(new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10));
      setEndDate(new Date(t.getFullYear(), t.getMonth(), 15).toISOString().slice(0, 10));
    } else if (preset === "last15") {
      setStartDate(new Date(t.getFullYear(), t.getMonth(), 16).toISOString().slice(0, 10));
      setEndDate(new Date(t.getFullYear(), t.getMonth() + 1, 0).toISOString().slice(0, 10));
    }
  };

  // PHP currency formatter
  const peso = (n) =>
    typeof n === "number"
      ? `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "₱0.00";

  // Group history by month for the "Previous Cutoffs" view
  const historyByMonth = useMemo(() => {
    if (!allTimeData?.records) return [];
    const groups = {};
    for (const rec of allTimeData.records) {
      const monthKey = rec.date?.slice(0, 7) || "unknown";
      if (!groups[monthKey]) {
        groups[monthKey] = {
          month: monthKey,
          records: [],
          pay25: 0,
          pay30: 0,
          bonus: 0,
          additional: 0,
          total: 0,
        };
      }
      groups[monthKey].records.push(rec);
      groups[monthKey].pay25 += (rec.classes_25min || 0) * (allTimeData.rate_25min || 0);
      groups[monthKey].pay30 += (rec.classes_30min || 0) * (allTimeData.rate_30min || 0);
      groups[monthKey].bonus += rec.demo_bonus || 0;
      const addRates = allTimeData.additional_rates || [];
      for (const r of addRates) {
        const cnt = rec.additional_classes?.[r.name] || 0;
        groups[monthKey].additional += cnt * (r.rate || 0);
      }
    }
    Object.values(groups).forEach((g) => {
      g.total = g.pay25 + g.pay30 + g.bonus + g.additional;
    });
    return Object.values(groups).sort((a, b) => b.month.localeCompare(a.month));
  }, [allTimeData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) return null;

  if (!data.linked) {
    return (
      <div className="max-w-2xl">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2 mb-4">
          <Wallet className="w-7 h-7 text-emerald-600" /> My Salary
        </h1>
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <Wallet className="w-12 h-12 text-slate-300 mx-auto" />
            <p className="text-slate-600">{data.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const s = data.summary;
  const additionalRows = Object.entries(s.additional_totals || {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1
          className="text-3xl font-bold text-slate-900 flex items-center gap-2"
          data-testid="my-salary-title"
        >
          <Wallet className="w-7 h-7 text-emerald-600" /> My Salary
        </h1>
        <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5" />
          View-only payroll for <strong>{data.teacher_name}</strong>. All amounts in
          Philippine Pesos (₱). Only admins can edit these records.
        </p>
      </div>

      {/* Cutoff selector */}
      <Card>
        <CardContent className="p-4 sm:p-5">
          <p className="font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-slate-500" /> Cutoff Period
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={() => setPreset("thisMonth")} data-testid="preset-this-month">
              This month
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("lastMonth")} data-testid="preset-last-month">
              Last month
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("first15")} data-testid="preset-first-half">
              Day 1–15
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("last15")} data-testid="preset-second-half">
              Day 16–end
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <div className="flex-1 min-w-0">
              <label className="text-xs font-medium text-slate-600">From</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="cutoff-start-date"
              />
            </div>
            <div className="flex-1 min-w-0">
              <label className="text-xs font-medium text-slate-600">To</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="cutoff-end-date"
              />
            </div>
            <Button
              onClick={load}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="cutoff-apply-btn"
            >
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Total earnings card — eye-catching */}
      <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">
                Total Earnings (this cutoff)
              </p>
              <p
                className="text-4xl sm:text-5xl font-bold text-emerald-700 mt-1"
                data-testid="total-earnings"
              >
                {peso(s.total_earnings)}
              </p>
              <p className="text-xs text-slate-500 mt-2">
                {startDate} → {endDate} · {data.records.length} day{data.records.length === 1 ? "" : "s"} recorded
              </p>
            </div>
            <TrendingUp className="w-10 h-10 text-emerald-300 hidden sm:block" />
          </div>
        </CardContent>
      </Card>

      {/* Breakdown grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
              25-min classes
            </p>
            <p className="text-2xl font-bold text-slate-900 mt-1">
              {s.total_25min_classes}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              × {peso(s.rate_25min)} = <strong className="text-slate-800">{peso(s.pay_25min)}</strong>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
              30-min classes
            </p>
            <p className="text-2xl font-bold text-slate-900 mt-1">
              {s.total_30min_classes}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              × {peso(s.rate_30min)} = <strong className="text-slate-800">{peso(s.pay_30min)}</strong>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
              Demo bonus
            </p>
            <p className="text-2xl font-bold text-slate-900 mt-1">
              {peso(s.total_demo_bonus)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {s.total_cancelled} cancelled class{s.total_cancelled === 1 ? "" : "es"}
            </p>
          </CardContent>
        </Card>

        {additionalRows.map(([name, info]) => (
          <Card key={name}>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                {name}
              </p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{info.count}</p>
              <p className="text-xs text-slate-500 mt-1">
                × {peso(info.rate)} = <strong className="text-slate-800">{peso(info.pay)}</strong>
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Daily breakdown */}
      <Card>
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
            <Receipt className="w-4 h-4 text-slate-500" />
            <p className="font-semibold text-slate-900">Daily Breakdown</p>
          </div>
          {data.records.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">
              No salary records in this period.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-2.5">Date</th>
                    <th className="px-4 py-2.5 text-center">25-min</th>
                    <th className="px-4 py-2.5 text-center">30-min</th>
                    <th className="px-4 py-2.5 text-center">Cancelled</th>
                    <th className="px-4 py-2.5 text-right">Demo bonus</th>
                    <th className="px-4 py-2.5 text-right">Day total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.records.map((r) => {
                    const dayTotal =
                      (r.classes_25min || 0) * data.rate_25min +
                      (r.classes_30min || 0) * data.rate_30min +
                      (r.demo_bonus || 0) +
                      (data.additional_rates || []).reduce(
                        (acc, ar) =>
                          acc + (r.additional_classes?.[ar.name] || 0) * (ar.rate || 0),
                        0
                      );
                    return (
                      <tr key={r.id} className="hover:bg-slate-50" data-testid={`salary-row-${r.id}`}>
                        <td className="px-4 py-2.5 font-medium text-slate-900">{r.date}</td>
                        <td className="px-4 py-2.5 text-center">{r.classes_25min || 0}</td>
                        <td className="px-4 py-2.5 text-center">{r.classes_30min || 0}</td>
                        <td className="px-4 py-2.5 text-center text-slate-500">
                          {r.cancelled || 0}
                        </td>
                        <td className="px-4 py-2.5 text-right">{peso(r.demo_bonus || 0)}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-emerald-700">
                          {peso(dayTotal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Previous cutoffs (expandable) */}
      <Card>
        <CardContent className="p-0">
          <button
            onClick={() => {
              setExpandHistory((v) => !v);
              if (!expandHistory) loadAllTime();
            }}
            className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
            data-testid="toggle-history-btn"
          >
            <div className="flex items-center gap-2">
              {expandHistory ? (
                <ChevronDown className="w-4 h-4 text-slate-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-500" />
              )}
              <p className="font-semibold text-slate-900 text-left">
                Previous Cutoff History
              </p>
            </div>
            <span className="text-xs text-slate-500">
              All previous months
            </span>
          </button>
          {expandHistory && (
            <div className="border-t border-slate-100">
              {allTimeLoading ? (
                <div className="py-10 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : historyByMonth.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-10">
                  No history found yet.
                </p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {historyByMonth.map((g) => (
                    <div
                      key={g.month}
                      className="px-5 py-3 flex items-center justify-between hover:bg-slate-50"
                      data-testid={`history-month-${g.month}`}
                    >
                      <div>
                        <p className="font-semibold text-slate-900">
                          {new Date(g.month + "-01").toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                          })}
                        </p>
                        <p className="text-xs text-slate-500">
                          {g.records.length} day{g.records.length === 1 ? "" : "s"} recorded
                        </p>
                      </div>
                      <p
                        className={cn(
                          "text-lg font-bold",
                          g.total > 0 ? "text-emerald-700" : "text-slate-400"
                        )}
                      >
                        {peso(g.total)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer reminder */}
      <p className="text-xs text-slate-400 text-center pb-2">
        This page is view-only. If you spot an error, please contact your admin.
      </p>
    </div>
  );
}
